import React, { useMemo } from "react";
import strings from "translate/language";

const SOURCE_PALETTE = [
  "#4466f2",
  "#0ea5e9",
  "#14b8a6",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
  "#64748b",
  "#06b6d4",
  "#84cc16",
  "#f97316",
];

const MAX_SOURCE_SLICES = 10;

const truncateName = (name, max = 48) => {
  if (!name) return "—";
  return name.length > max ? `${name.slice(0, max)}…` : name;
};

const cleanSourceLabel = (raw) => {
  if (raw == null) return null;
  const label = String(raw).trim();
  if (
    !label ||
    label.toLowerCase() === "null" ||
    label.toLowerCase() === "undefined"
  ) {
    return null;
  }
  return label;
};

const buildSourceSlices = (otherSources) => {
  if (!otherSources || typeof otherSources !== "object") return [];

  const entries = Object.entries(otherSources)
    .map(([key, count]) => {
      const label = cleanSourceLabel(key) || "Unknown";
      return { label, count: Number(count) || 0 };
    })
    .filter((e) => e.count > 0)
    .sort((a, b) => b.count - a.count);

  if (!entries.length) return [];

  if (entries.length <= MAX_SOURCE_SLICES) return entries;

  const top = entries.slice(0, MAX_SOURCE_SLICES - 1);
  const rest = entries.slice(MAX_SOURCE_SLICES - 1);
  const otherCount = rest.reduce((sum, e) => sum + e.count, 0);
  return [...top, { label: "Other", count: otherCount }];
};

const DashboardInsights = ({ data }) => {
  const topCps = data?.channePartners?.top_ten_cps || [];
  const bookingConversion = data?.bookings?.booking_conversion;
  const propertyConversions = data?.bookings?.property_conversions || [];
  const propertyBookings = useMemo(() => {
    const rows = data?.bookings?.property_bookings || [];
    return [...rows].sort(
      (a, b) => (b.totalBookings || 0) - (a.totalBookings || 0)
    );
  }, [data?.bookings?.property_bookings]);

  const sourceSlices = useMemo(
    () => buildSourceSlices(data?.customers?.other_sources),
    [data?.customers?.other_sources]
  );

  const sourceTotal = useMemo(
    () => sourceSlices.reduce((sum, s) => sum + s.count, 0),
    [sourceSlices]
  );

  const sourceMax = useMemo(
    () => Math.max(...sourceSlices.map((s) => s.count), 1),
    [sourceSlices]
  );

  return (
    <div className="justo-dashboard__insights">
      <div className="justo-dashboard__insights-row">
        <div className="justo-dashboard__insight-panel justo-dashboard__insight-panel--wide">
          <div className="justo-dashboard__insight-head">
            <div className="justo-dashboard__insight-titles">
              <h5 className="justo-dashboard__insight-title">
                Top Ten Channel Partners
              </h5>
              <p className="justo-dashboard__insight-subtitle">
                By visitors created
              </p>
            </div>
          </div>
          <div className="justo-dashboard__insight-body justo-dashboard__insight-body--scroll">
            {topCps.length > 0 ? (
              <ul className="justo-dashboard__rank-list">
                {topCps.map((partner, index) => (
                  <li
                    key={partner.cp_id || `${partner.name}-${index}`}
                    className="justo-dashboard__rank-item"
                  >
                    <span
                      className={`justo-dashboard__rank-badge${
                        index < 3 ? " is-top" : ""
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span
                      className="justo-dashboard__rank-name"
                      title={partner.name}
                    >
                      {truncateName(partner.name)}
                    </span>
                    <span className="justo-dashboard__rank-metric">
                      {(partner.lead_count ?? 0).toLocaleString()}
                      <small>visitors</small>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="justo-dashboard__chart-empty">
                {strings.notavailable}
              </p>
            )}
          </div>
        </div>

        <div className="justo-dashboard__insight-panel">
          <div className="justo-dashboard__insight-head">
            <div className="justo-dashboard__insight-titles">
              <h5 className="justo-dashboard__insight-title">
                Visitors by Other Sources
              </h5>
              <p className="justo-dashboard__insight-subtitle">
                {sourceTotal > 0
                  ? `${sourceTotal.toLocaleString()} total`
                  : "Non–channel partner sources"}
              </p>
            </div>
          </div>
          <div className="justo-dashboard__insight-body justo-dashboard__insight-body--fill">
            {sourceSlices.length > 0 ? (
              <ul
                className="justo-dashboard__source-bars"
                aria-label={`Visitors by other sources. Total ${sourceTotal.toLocaleString()}`}
              >
                {sourceSlices.map((slice, index) => {
                  const pct =
                    sourceTotal > 0
                      ? ((slice.count / sourceTotal) * 100).toFixed(1)
                      : "0.0";
                  const barWidth = Math.max(
                    (slice.count / sourceMax) * 100,
                    4
                  );
                  const color = SOURCE_PALETTE[index % SOURCE_PALETTE.length];
                  return (
                    <li
                      key={`${slice.label}-${index}`}
                      className="justo-dashboard__source-bar"
                    >
                      <div className="justo-dashboard__source-bar-meta">
                        <span
                          className="justo-dashboard__source-bar-name"
                          title={slice.label}
                        >
                          <span
                            className="justo-dashboard__source-swatch"
                            style={{ background: color }}
                            aria-hidden="true"
                          />
                          {slice.label}
                        </span>
                        <span className="justo-dashboard__source-bar-stats">
                          <strong>{slice.count.toLocaleString()}</strong>
                          <span>{pct}%</span>
                        </span>
                      </div>
                      <div
                        className="justo-dashboard__source-bar-track"
                        role="img"
                        aria-label={`${slice.label}: ${slice.count.toLocaleString()} visitors, ${pct} percent`}
                      >
                        <div
                          className="justo-dashboard__source-bar-fill"
                          style={{
                            width: `${barWidth}%`,
                            background: color,
                          }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="justo-dashboard__chart-empty">
                {strings.notavailable}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="justo-dashboard__insights-row">
        <div className="justo-dashboard__insight-panel">
          <div className="justo-dashboard__insight-head">
            <div className="justo-dashboard__insight-titles">
              <h5 className="justo-dashboard__insight-title">
                Booking Conversion of Properties
              </h5>
              <p className="justo-dashboard__insight-subtitle">
                In terms of lead
              </p>
            </div>
            {bookingConversion != null && bookingConversion !== "" ? (
              <div className="justo-dashboard__insight-stat">
                <span className="justo-dashboard__insight-stat-label">
                  Overall
                </span>
                <span className="justo-dashboard__insight-stat-value">
                  {bookingConversion}%
                </span>
              </div>
            ) : null}
          </div>
          <div className="justo-dashboard__insight-body justo-dashboard__insight-body--scroll">
            {propertyConversions.length > 0 ? (
              <ul className="justo-dashboard__convert-list">
                {propertyConversions.map((property, index) => {
                  const rate = Math.min(
                    Number(property.conversionRate) || 0,
                    100
                  );
                  return (
                    <li
                      key={property.property_id || index}
                      className="justo-dashboard__convert-item"
                    >
                      <div className="justo-dashboard__convert-meta">
                        <span
                          className="justo-dashboard__convert-name"
                          title={property.property_title}
                        >
                          {truncateName(
                            property.property_title || `Property ${index + 1}`,
                            42
                          )}
                        </span>
                        <span className="justo-dashboard__convert-rate">
                          {property.conversionRate}%
                        </span>
                      </div>
                      <div
                        className="justo-dashboard__convert-track"
                        role="progressbar"
                        aria-valuenow={rate}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div
                          className="justo-dashboard__convert-fill"
                          style={{ width: `${rate}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="justo-dashboard__chart-empty">
                {strings.notavailable}
              </p>
            )}
          </div>
        </div>

        <div className="justo-dashboard__insight-panel">
          <div className="justo-dashboard__insight-head">
            <div className="justo-dashboard__insight-titles">
              <h5 className="justo-dashboard__insight-title">Top Properties</h5>
              <p className="justo-dashboard__insight-subtitle">
                By booking count
              </p>
            </div>
          </div>
          <div className="justo-dashboard__insight-body justo-dashboard__insight-body--scroll">
            {propertyBookings.length > 0 ? (
              <div className="justo-dashboard__insight-table-wrap">
                <table className="table justo-dashboard__insight-table">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Property</th>
                      <th scope="col" className="text-right">
                        Bookings
                      </th>
                      <th scope="col" className="text-right">
                        Lead Conv.%
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {propertyBookings.map((row, index) => (
                      <tr key={row.property_id || index}>
                        <td>
                          <span
                            className={`justo-dashboard__rank-badge${
                              index < 3 ? " is-top" : ""
                            }`}
                          >
                            {index + 1}
                          </span>
                        </td>
                        <td title={row.property_title}>
                          {truncateName(row.property_title, 36)}
                        </td>
                        <td className="text-right justo-dashboard__insight-num">
                          {row.totalBookings ?? 0}
                        </td>
                        <td className="text-right justo-dashboard__insight-muted">
                          {row.conversionFlatRate != null
                            ? `${row.conversionFlatRate}%`
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="justo-dashboard__chart-empty">
                {strings.notavailable}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardInsights;
